/**
 * src/shell/agents/components/GenerativeDashboardCanvas.tsx
 *
 * Responsive 12-Column Generative UI Dashboard Canvas.
 * Hosts dynamic widgets synthesized from natural language with in-place AST mutations,
 * 20-step undo/redo controls, and isolated AppErrorBoundary sandboxes.
 */

import React, { useState } from 'react';
import { ICanvasDashboardSpec, IGenUIWidgetSpec } from '../types/genui-types';
import { genUISynthesizer } from '../services/GenUISynthesizer';
import { canvasMutationEngine } from '../services/CanvasMutationEngine';
import { AppErrorBoundary } from '../../components/AppErrorBoundary';
import { KPICardWidget } from './widgets/KPICardWidget';
import { AccessHeatmapWidget } from './widgets/AccessHeatmapWidget';
import { CreditSankeyWidget } from './widgets/CreditSankeyWidget';
import { MeshTopologyWidget } from './widgets/MeshTopologyWidget';
import { AccessTimelineWidget } from './widgets/AccessTimelineWidget';
import { CopilotRibbon } from './CopilotRibbon';
import { multiAccountVault } from '../../auth/multi-account-vault';
import { UserRole } from '../../../main/security/RoleEnforcement';

export interface GenerativeDashboardCanvasProps {
  initialPrompt?: string;
  onClose?: () => void;
}

export const GenerativeDashboardCanvas: React.FC<GenerativeDashboardCanvasProps> = ({
  initialPrompt = 'Build me a dashboard for server room access and credit flow',
  onClose,
}) => {
  const [promptInput, setPromptInput] = useState('');
  const [activeSpec, setActiveSpec] = useState<ICanvasDashboardSpec>(() => {
    const activeAccount = multiAccountVault.getActiveAccount();
    return genUISynthesizer.synthesizeDashboard(initialPrompt, {
      userId: activeAccount?.accountId || 'demo_user',
      role: (activeAccount?.role as UserRole) || 'ordinary_user',
    });
  });
  const [statusMessage, setStatusMessage] = useState<string>('Dashboard initialized.');

  const handleSynthesizeNew = (prompt: string) => {
    if (!prompt.trim()) return;
    const activeAccount = multiAccountVault.getActiveAccount();
    const newSpec = genUISynthesizer.synthesizeDashboard(prompt, {
      userId: activeAccount?.accountId || 'demo_user',
      role: (activeAccount?.role as UserRole) || 'ordinary_user',
    });
    setActiveSpec(newSpec);
    setStatusMessage(`Synthesized new dashboard: "${newSpec.title}"`);
    setPromptInput('');
  };

  const handleApplyMutation = (instruction: string) => {
    if (!instruction.trim()) return;
    const result = canvasMutationEngine.mutate(activeSpec, instruction);
    setActiveSpec(result.mutatedSpec);
    setStatusMessage(result.description);
    setPromptInput('');
  };

  const handleUndo = () => {
    const prev = canvasMutationEngine.undo(activeSpec);
    if (prev) {
      setActiveSpec(prev);
      setStatusMessage('Reverted to previous canvas state.');
    }
  };

  const handleRedo = () => {
    const next = canvasMutationEngine.redo(activeSpec);
    if (next) {
      setActiveSpec(next);
      setStatusMessage('Restored canvas state.');
    }
  };

  const renderWidget = (spec: IGenUIWidgetSpec) => {
    switch (spec.type) {
      case 'kpi_card':
        return <KPICardWidget spec={spec} />;
      case 'access_heatmap':
        return <AccessHeatmapWidget spec={spec} />;
      case 'credit_sankey':
        return <CreditSankeyWidget spec={spec} />;
      case 'mesh_topology':
        return <MeshTopologyWidget spec={spec} />;
      case 'access_timeline':
        return <AccessTimelineWidget spec={spec} />;
      default:
        return (
          <div style={{ padding: '16px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
            Unknown widget type: {spec.type}
          </div>
        );
    }
  };

  return (
    <div
      data-testid="generative-dashboard-canvas"
      style={{
        background: 'rgba(10, 15, 29, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        padding: '24px',
        color: '#f8fafc',
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
      }}
    >
      {/* Header & Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🎨</span>
            <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>
              {activeSpec.title}
            </h2>
            <span
              style={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#38bdf8',
                background: 'rgba(56, 189, 248, 0.15)',
                padding: '2px 8px',
                borderRadius: '6px',
              }}
            >
              v{activeSpec.version}
            </span>
          </div>
          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '4px 0 0 0' }}>
            {statusMessage}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            data-testid="btn-canvas-undo"
            onClick={handleUndo}
            disabled={!canvasMutationEngine.canUndo()}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: canvasMutationEngine.canUndo() ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.02)',
              color: canvasMutationEngine.canUndo() ? '#f8fafc' : '#64748b',
              fontSize: '12px',
              cursor: canvasMutationEngine.canUndo() ? 'pointer' : 'not-allowed',
            }}
          >
            ↩ Undo
          </button>
          <button
            type="button"
            data-testid="btn-canvas-redo"
            onClick={handleRedo}
            disabled={!canvasMutationEngine.canRedo()}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: canvasMutationEngine.canRedo() ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.02)',
              color: canvasMutationEngine.canRedo() ? '#f8fafc' : '#64748b',
              fontSize: '12px',
              cursor: canvasMutationEngine.canRedo() ? 'pointer' : 'not-allowed',
            }}
          >
            ↪ Redo
          </button>
          {onClose && (
            <button
              type="button"
              data-testid="btn-canvas-close"
              onClick={onClose}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                color: '#94a3b8',
                padding: '6px 12px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Close ✕
            </button>
          )}
        </div>
      </div>

      {/* Proactive CoPilot Ribbon */}
      <CopilotRibbon onApplyMutation={handleApplyMutation} />

      {/* Prompt Command Bar for Synthesis or Incremental Mutation */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <input
          type="text"
          data-testid="canvas-prompt-input"
          value={promptInput}
          onChange={(e) => setPromptInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (promptInput.toLowerCase().startsWith('make') || promptInput.toLowerCase().startsWith('add') || promptInput.toLowerCase().startsWith('remove')) {
                handleApplyMutation(promptInput);
              } else {
                handleSynthesizeNew(promptInput);
              }
            }
          }}
          placeholder="Type instruction (e.g. 'Make heatmap crimson', 'Add Gossip Mesh Topology', 'Build cluster overview')..."
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.05)',
            color: '#f8fafc',
            fontSize: '13px',
            outline: 'none',
          }}
        />
        <button
          type="button"
          data-testid="btn-apply-mutation"
          onClick={() => {
            if (promptInput.toLowerCase().startsWith('make') || promptInput.toLowerCase().startsWith('add') || promptInput.toLowerCase().startsWith('remove')) {
              handleApplyMutation(promptInput);
            } else {
              handleSynthesizeNew(promptInput);
            }
          }}
          style={{
            padding: '12px 20px',
            borderRadius: '12px',
            border: 'none',
            background: 'linear-gradient(135deg, #a855f7, #6366f1)',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Apply Mutation 🪄
        </button>
      </div>

      {/* 12-Column Responsive CSS Grid Container */}
      <div
        data-testid="generative-widget-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: '16px',
          minHeight: '400px',
        }}
      >
        {activeSpec.widgets.map((w) => (
          <div
            key={w.id}
            data-testid={`grid-item-${w.id}`}
            style={{
              gridColumn: `span ${Math.min(12, Math.max(1, w.layout.w))}`,
              minHeight: '160px',
            }}
          >
            <AppErrorBoundary>
              {renderWidget(w)}
            </AppErrorBoundary>
          </div>
        ))}
      </div>
    </div>
  );
};
